import '../components/common/RichTextEditor/RichTextEditor.scss';
import { Box, Container, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import ReplacementDropdown from '../components/common/EmailTemplatesSection/ReplacementDropdown';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import DEFAULT_EXTENSIONS from '../components/common/RichTextEditor/DefaultExtensions';
import MenuBar from '../components/common/RichTextEditor/MenuBar';
import TagsSection from '../components/common/TagsSection/TagsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import DisableContextProvider, { DisableContext } from '../contexts/DisableContextProvider';
import usePermissionAllowed from '../hooks/usePermissionAllowed';
import { ROUTE_PATHS } from '../routes/Routes';
import { useCreateEmailTemplate, useGetEmailTemplateById, useUpdateEmailTemplate } from '../services/react-query/email_templates';
import { useArchiveEmailTemplate } from '../services/react-query/email_templates/useArchiveEmailTemplate';
import { useDuplicateEmailTemplate } from '../services/react-query/email_templates/useDuplicateEmailTemplate';
import { mutationKeys } from '../services/react-query/mutationKeys';
import { Actions } from '../services/react-query/permissions';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { EmailTemplate } from '../types/EmailTemplate';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator } from '../utility/inputUtils';
import DuplicateModal from '../components/DuplicateModal';

export default function EmailTemplateItem() {
  const { uuid } = useParams();

  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const { data, isLoading } = useGetEmailTemplateById({ uuid });
  const navigate = useNavigate();

  const onSuccessCallback = () => setSaved(true);
  const { mutate: createEmailTemplate } = useCreateEmailTemplate({ onSuccessCallback });
  const { mutate: updateEmailTemplate } = useUpdateEmailTemplate({ uuid, onSuccessCallback });
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const isCreateEmailTemplateMutation = useIsMutating({ mutationKey: [mutationKeys.createEmailTemplate] });
  const isUpdateEmailTemplateMutation = useIsMutating({ mutationKey: [mutationKeys.updateEmailTemplate] });

  const { mutate: duplicateTemplateMutation } = useDuplicateEmailTemplate((newUuid) => {
    navigate(`/email-templates/${newUuid}`);
  });

  const editor = useEditor({ extensions: DEFAULT_EXTENSIONS });
  const defaultInsertFunc = (insertString: string) => editor?.chain().focus().insertContent(insertString).run();
  const [onInsert, setOnInsert] = useState<(inputString: string) => void>(defaultInsertFunc);

  const { mutate: archiveEmailTemplate } = useArchiveEmailTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.EMAIL_TEMPLATES}`),
  });

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable(!!emailTemplate?.discarded_at);

  // Sets the editor content when the email template data is loaded
  useEffect(() => {
    if (!data) return;

    setEmailTemplate(data);
    editor?.commands?.setContent(data.content);
  }, [data]);

  if (!emailTemplate || isLoading) return <Loading />;

  const handleSave = () => {
    const content = editor?.getHTML();
    if (!content) return;

    const saveData = { ...emailTemplate, content };
    if (uuid === 'new') {
      createEmailTemplate(saveData);
    } else {
      updateEmailTemplate(saveData);
    }
  };

  const handleDuplicate = (name: string) => {
    duplicateTemplateMutation({ uuid: _.toString(uuid), name });
  };

  const handleSubjectFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const subject = emailTemplate.subject || '';

    const start = event.target?.selectionStart || 0;
    const end = event.target?.selectionEnd || 0;

    const prefix = subject.slice(0, start);
    const postfix = subject.slice(end);

    const newFunc = (insertString: string) =>
      setEmailTemplate({
        ...emailTemplate,
        subject: [prefix, insertString, postfix].join(''),
      });

    setOnInsert(() => newFunc);
  };

  const handleEditorFocus = () => {
    setOnInsert(() => defaultInsertFunc);
  };

  return (
    <Container>
      <ItemHeader
        title={'Email Template'}
        onSave={handleSave}
        archived={!!emailTemplate.discarded_at}
        disableActions={Boolean(isCreateEmailTemplateMutation || isUpdateEmailTemplateMutation)}
        showMoreOptions={
          uuid !== 'new' ? (
            <MoreOptions
              onArchive={archiveEmailTemplate}
              onDuplicate={() => setIsDuplicateModalOpen(true)}
              name={emailTemplate?.name}
              itemName="email template"
            />
          ) : null
        }
      />
      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.EMAIL_TEMPLATE_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              onChange={(event) => setEmailTemplate({ ...emailTemplate, name: event.target.value })}
              value={emailTemplate?.name ?? ''}
              disabled={disabled}
              {...errorPopulator(emailTemplate, 'name')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={(event) => setEmailTemplate({ ...emailTemplate, description: event.target.value })}
              value={emailTemplate?.description ?? ''}
              disabled={disabled}
              {...errorPopulator(emailTemplate, 'description')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="reply_to"
              label="Reply To"
              onChange={(event) => setEmailTemplate({ ...emailTemplate, reply_to: event.target.value })}
              value={emailTemplate?.reply_to ?? ''}
              disabled={disabled}
              {...errorPopulator(emailTemplate, 'reply_to')}
            />
          </Box>
        </Box>
      </AccordionSection>
      <AccordionSection title="Template Contents" collapseId={UserSettingsCollapseKeys.EMAIL_TEMPLATE_ITEM_CONTENT}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={inputContainerStyling}>
            <Box sx={inputDividerStyling}>
              <TextField
                id="subject"
                label="Subject"
                onChange={(event) => setEmailTemplate({ ...emailTemplate, subject: event.target.value })}
                value={emailTemplate?.subject ?? ''}
                disabled={disabled}
                {...errorPopulator(emailTemplate, 'subject')}
                slotProps={{
                  htmlInput: {
                    onFocus: handleSubjectFocus,
                    onSelect: handleSubjectFocus,
                  },
                }}
              />
            </Box>
            <Box sx={inputDividerStyling}>
              <ReplacementDropdown onInsert={onInsert} />
            </Box>
          </Box>

          <Box sx={{ gap: 2, ...inputDividerStyling }}>
            <EditorContext.Provider value={{ editor }}>
              <MenuBar />
              <EditorContent className="RichTextEditor" editor={editor} onFocus={handleEditorFocus} />
            </EditorContext.Provider>
          </Box>
        </Box>
      </AccordionSection>

      <TagsSection
        tags={emailTemplate.tags}
        collapseId={UserSettingsCollapseKeys.EMAIL_TEMPLATE_ITEM_TAGS}
        setTags={(tags) => setEmailTemplate({ ...emailTemplate, tags })}
      />
      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(newName) => {
          setIsDuplicateModalOpen(false);
          handleDuplicate(newName);
        }}
        defaultName={`${emailTemplate?.name ?? ''} Copy`}
        itemName="email template"
      />
      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Container>
  );
}
